import * as d3 from 'd3';
import {
  BAR_CHART_DATASET_TYPES,
  BAR_CHART_X_AXIS_TYPES,
  colors,
  findTextXCollisions,
  findTextYCollisions,
  findTickTitle,
  reset,
  tooltips,
} from './utils/index.js';

import {
  handleLegendMouseOut,
  handleLegendMouseOver,
} from './utils/chartLegend.js';

const BarChart = (
  chartRef,
  legendRef,
  initialData,
  chartData,
  chartWidth,
  reportOptions
) => {
  const { percentage, group, dataset } = reportOptions;
  const initialHeight = 350;
  const highestYValue = percentage
    ? d3.max(initialData, (d) => d.yPercentageValue)
    : d3.max(initialData, (d) => d.yValue);
  const selectedYValue = (d) =>
    percentage ? d.values[0].yPercentageValue : d.values[0].yValue;
  const textYCollisions = findTextYCollisions(highestYValue, initialHeight);
  const textXCollisions = (width) =>
    findTextXCollisions(initialData, chartData, width);
  const findDataset = (xDatasetId) =>
    initialData.find((item) => item.xDatasetId === Number(xDatasetId));
  const datasetName = (xDatasetId) =>
    findDataset(xDatasetId) ? findDataset(xDatasetId).xDataset : '';
  const selectedGroupSingular = BAR_CHART_X_AXIS_TYPES.find(
    (x) => x.value === group
  ).singular;
  const selectedDatasetSingular = BAR_CHART_DATASET_TYPES.find(
    (d) => d.value === dataset
  ).singular;
  const isChartEmpty = !initialData.length || !highestYValue;
  const margin = {
    top: 40,
    right: 60,
    bottom: 28,
    left: 34,
  };
  const width = chartWidth - 30 - margin.left - margin.right;
  const height = initialHeight + textYCollisions - margin.top - margin.bottom;

  const chart = d3.select(chartRef.current);
  const legend = d3.select(legendRef.current);

  reset(chart, legend); // Clearing all old dom/data before next render

  const svg = chart
    .append('svg')
    .attr('width', width + margin.left + margin.right)
    .attr(
      'height',
      height + margin.top + margin.bottom + textXCollisions(width) * 0.5
    )
    .append('g')
    .attr('transform', `translate(${margin.left},${margin.top})`);

  const types = Array.from(new Set(initialData.map((d) => d.xDatasetId)));

  const x0Scale = d3
    .scaleBand()
    .domain(chartData.map((d) => d.key))
    .rangeRound([0, width])
    .paddingInner(0.22);

  const x1Scale = d3
    .scaleBand()
    .domain(types)
    .rangeRound([0, x0Scale.bandwidth()])
    .padding(0.03);

  const yScale = d3
    .scaleLinear()
    .domain([0, highestYValue])
    .nice()
    .range([height, 0]);

  // Create a color map based on unique xDataset values
  const colorMap = {};

  types.forEach((type, index) => {
    colorMap[type] = colors[index % colors.length];
  });
  const tooltip = d3
    .select('body')
    .append('div')
    .attr('id', 'tooltip')
    .style('display', 'none');

  // Create bars
  svg
    .selectAll('.bar')
    .data(chartData)
    .enter()
    .append('g')
    .attr('class', 'bar')
    .attr('transform', (d) => `translate(${x0Scale(d.key)},0)`)
    .selectAll('rect')
    .data((d) => d.values)
    .enter()
    .append('rect')
    .attr('class', (d) => `value value-${d.key}`)
    .attr('x', (d) => x1Scale(d.key))
    .attr('y', (d) => yScale(selectedYValue(d)))
    .attr('width', x1Scale.bandwidth())
    .attr('height', (d) =>
      !isChartEmpty ? height - yScale(selectedYValue(d)) : 0
    )
    .attr('fill', (d) => d.values[0].color || colorMap[d.key])
    .on('mousemove', (d) =>
      tooltips(
        d,
        tooltip,
        width,
        selectedGroupSingular,
        selectedDatasetSingular,
        datasetName,
        percentage,
        types.length
      )
    )
    .on('mouseout', () => tooltip.style('display', 'none'));

  const legendItems = legend
    .selectAll('.legend-item')
    .data(types)
    .enter()
    .append('div')
    .attr('class', 'legend-item')
    .style('display', (d) => d === null && 'none');

  legendItems
    .append('div')
    .attr('class', 'legend-label')
    .text((d) => `${datasetName(d)}${percentage ? ' %' : ''}`);

  legendItems
    .append('div')
    .attr('class', 'legend-color')
    .style('background-color', (d) => {
      const customColor = initialData.find((i) => i.xDatasetId === d).color;
      return customColor || colorMap[d];
    });

  legendItems
    .on('mouseover', (d) => handleLegendMouseOver(d, chart))
    .on('mouseout', (d) => handleLegendMouseOut(d, chart));

  // Create x-axis
  svg
    .append('g')
    .attr('class', 'x-axis')
    .attr('transform', `translate(0,${height})`)
    .call(
      d3
        .axisBottom(x0Scale)
        .tickFormat((groupKey) => findTickTitle(initialData, groupKey))
    );
  svg
    .selectAll('text') // Select all x-axis tick text
    .style('text-anchor', textXCollisions(width) ? 'start' : 'center')
    .style('transform', textXCollisions(width) ? 'rotate(30deg)' : '');

  // Create y-axis
  svg
    .append('g')
    .attr('class', 'y-axis')
    .call(
      d3
        .axisLeft(yScale)
        .ticks(
          highestYValue > 20 ? Math.ceil(highestYValue / 5) : highestYValue
        )
        .tickFormat((d) => (percentage ? `${d}%` : d))
    );
};

export default BarChart;
